// Rendered value of the Default column; keyed into the shared status
// badge tones via @shared/lib/getStatusBadge (camelCased to 'default').
export const PROVIDER_DEFAULT_STATUSES = {
  default: 'default',
};

// Field limits shared by the add (POST) and edit (PATCH) forms —
// mirrors the backend FluentValidation for both routes. Lives on the
// entity so neither feature has to import from the other.
export const PROVIDER_FIELD_LIMITS = {
  nameMax: 64,
  titleMax: 64,
  npiLength: 10,
};

/**
 * Standard credentials, used ONLY until
 * GET /accounts/{uuid}/providers/titles answers, and if it never does
 * (see useProviderTitles).
 *
 * Not the source of truth: the backend owns the list so a credential
 * can be added without a dashboard release. This copy exists so the
 * dropdown is never empty — someone editing a provider's NPI should not
 * be stopped because a lookup failed. If it drifts from the server's
 * list, the server's wins the moment it answers.
 */
export const PROVIDER_TITLES_FALLBACK = ['MD', 'DO', 'APRN', 'PA'];

/**
 * The option value for a credential outside the list. Never stored: the
 * form sends the typed text, and an empty one means the provider has no
 * title, which the API accepts (the SMS then reads "John Smith at Acme"
 * with no suffix).
 */
export const PROVIDER_TITLE_OTHER = '__other__';

/**
 * Split a stored title into the dropdown's two parts.
 *
 * Matching is case-insensitive and ignores dots so legacy rows saved as
 * `md` or `M.D.` open on MD rather than falling into Other — the server
 * normalizes on write, but a row keeps its old spelling until someone
 * saves it again.
 *
 * The list is passed in rather than read from a constant: it comes from
 * the API, and a title the server knows but this build does not must
 * still open on its own option rather than landing in Other.
 *
 * @param {string} stored the `providerTitle` as the API returned it
 * @param {string[]} titles the standard credentials, in display order
 * @returns {{option: string, custom: string}} dropdown value + Other text
 */
export const splitProviderTitle = (stored, titles = PROVIDER_TITLES_FALLBACK) => {
  const trimmed = String(stored ?? '').trim();
  if (!trimmed) return { option: '', custom: '' };
  const comparable = trimmed.replace(/\./g, '').toUpperCase();
  const standard = (Array.isArray(titles) ? titles : []).find(
    title => String(title).replace(/\./g, '').toUpperCase() === comparable
  );
  if (standard) return { option: standard, custom: '' };
  return { option: PROVIDER_TITLE_OTHER, custom: trimmed };
};

/**
 * The value to store for a given dropdown state — the inverse of
 * `splitProviderTitle`.
 *
 * @param {string} option dropdown value
 * @param {string} custom text typed under Other
 * @returns {string} what to put in `providerTitle`
 */
export const joinProviderTitle = (option, custom) => {
  if (option === PROVIDER_TITLE_OTHER) return String(custom ?? '').trim();
  return option || '';
};
